import { capitalize, startCase } from "lodash-es";

import { IClientTinyDTO } from "./Client";
import { Base64QueryFilter, INumberSelectOption, IStringOption, IStringSelectOption } from "./Misc";
import { IPaginatedDTO } from "./PaginatedDTO";
import { IOperator, IPersonSnapshot, Operator, OperatorRoles } from "./Person";
import { MapFormats } from "@/helpers/StringHelpers";
import { ManualContactTaskStatus } from "@/services/DTOs/ManualContactOutcomeDTO";

export interface IRelatedTaskDTO {
  id: number,
  assignedBy?: IOperator,
  assignedTo?: IOperator,
  message?: string,
  created: string | Date,
  inProgressAt?: string;
  completed?: string | Date;
  completionComment?: string,
  completedBy?: IOperator
}

export enum TASK_ROLES {
  ASSIGNED_BY = "assignedBy",
  ASSIGNED_TO = "assignedTo",
  COMPLETED_BY = "completedBy"
}

export enum ComplianceResourceType {
  GENOME = "genome",
  TEAM_GENOME = "team-genome",
  JOB_POST = "job-post"
}

export interface ITaskContextDTO {
  messageId?: string,
  messageRecipientGgId?: string,
  messageSenderGgId?: string,
  roomId?: string,
  complianceResourceType?: ComplianceResourceType,
  complianceResourceId?: string,
  complianceResourceName?: string,
  manualContactRequestId?: number,
  manualContactExecutionId?: number,
  manualContactStageDeadline?: string,
  manualContactLinkedinUrl?: string,
  manualContactStatus?: ManualContactTaskStatus
};

export interface ITaskDTO {
  id: number,
  opportunityRefId: string,
  client?: IClientTinyDTO,
  userGgId?: string
  opportunityObjective?: string,
  opportunityReview?: string,
  opportunityPoster?: IPersonSnapshot,
  opportunityServiceTypes: number[];
  relatedTasks: IRelatedTaskDTO[],
  assignedBy?: IOperator,
  assignedTo?: IOperator,
  trigger: string,
  type: string,
  priority: number,
  subject?: string,
  message?: string,
  created: string | Date,
  inProgressAt?: string;
  deadline: string | Date,
  completed?: string | Date,
  completionComment?: string,
  completedBy?: IOperator,
  context?: ITaskContextDTO,
  messageTicketId?: number
}

export class RelatedTask implements IRelatedTaskDTO {
  id: number;
  assignedBy?: IOperator;
  assignedTo?: IOperator;
  message?: string;
  created: string | Date;
  inProgressAt?: string;
  completed?: string | Date;
  completionComment?: string;
  completedBy?: IOperator;

  constructor(data: IRelatedTaskDTO) {
    this.id = data.id;
    this.assignedBy = data.assignedBy;
    this.assignedTo = data.assignedTo;
    this.message = data.message;
    this.created = data.created;
    this.inProgressAt = data.inProgressAt;
    this.completed = data.completed;
    this.completionComment = data.completionComment;
    this.completedBy = data.completedBy;
  }

  public getRoleUserName(role: TASK_ROLES): string {
    switch (role) {
      case TASK_ROLES.ASSIGNED_BY:
        return this.assignedBy?.bio?.name ?? "System";

      case TASK_ROLES.ASSIGNED_TO:
        return this.assignedTo?.bio?.name ?? "Unknown";

      case TASK_ROLES.COMPLETED_BY:
        return this.completedBy?.bio?.name ?? "Unknown";

      default:
        return "Unknown";
    }
  }

  public getRoleName(operator: IOperator | undefined): string {
    if (operator) {
      return new Operator(operator).getRole();
    } else {
      return OperatorRoles.UNKNOWN;
    }
  }
};
export class TaskDTO extends RelatedTask implements ITaskDTO {
  id: number;
  opportunityRefId: string;
  client?: IClientTinyDTO;
  userGgId?: string;
  opportunityObjective?: string;
  opportunityReview?: string;
  opportunityServiceTypes: number[];
  opportunityPoster?: IPersonSnapshot;
  relatedTasks: RelatedTask[];
  assignedBy?: IOperator;
  assignedTo?: IOperator;
  trigger: string;
  type: string;
  priority: number;
  subject?: string;
  message?: string;
  created: string | Date;
  deadline: string | Date;
  inProgressAt?: string;
  completed?: string | Date;
  completionComment?: string;
  completedBy?: IOperator;
  context?: ITaskContextDTO;
  messageTicketId?: number;

  constructor(data: ITaskDTO) {
    super(data as IRelatedTaskDTO);
    this.id = data.id;
    this.opportunityRefId = data.opportunityRefId;
    this.client = data.client;
    this.userGgId = data.userGgId;
    this.opportunityObjective = data.opportunityObjective;
    this.opportunityReview = data.opportunityReview;
    this.opportunityServiceTypes = data.opportunityServiceTypes;
    this.opportunityPoster = data.opportunityPoster;
    this.relatedTasks = data.relatedTasks.map(el => new RelatedTask(el));
    this.assignedBy = data.assignedBy;
    this.assignedTo = data.assignedTo;
    this.trigger = data.trigger;
    this.type = data.type;
    this.priority = data.priority;
    this.subject = data.subject;
    this.message = data.message;
    this.created = data.created;
    this.deadline = data.deadline;
    this.inProgressAt = data.inProgressAt;
    this.completed = data.completed;
    this.completionComment = data.completionComment;
    this.completedBy = data.completedBy;
    this.context = data.context;
    this.messageTicketId = data.messageTicketId;
  }

  public static Test(): TaskDTO {
    return new TaskDTO({
      id: 1,
      opportunityRefId: "ref",
      opportunityObjective: "objective",
      opportunityReview: "approved",
      opportunityServiceTypes: [2],
      relatedTasks: [] as RelatedTask[],
      assignedBy: {} as IOperator,
      assignedTo: {} as IOperator,
      trigger: "c",
      type: "c",
      priority: 1,
      subject: "subject",
      message: "message",
      created: "2023-01-01T17:45:22.341Z",
      deadline: "2023-01-04T17:45:22.341Z",
      inProgressAt: "2023-01-02T17:45:22.341Z",
      completed: undefined,
      completionComment: undefined,
      completedBy: undefined
    } as ITaskDTO);
  }
};

export type SortableTaskKeys = "deadline" | "created";

export interface ITaskSortDTO {
  sortBy: SortableTaskKeys
  order: "asc" | "desc"
}

export interface ITaskFilter {
  id?: number,
  userGgId?: string,
  clientId?: number,
  opportunityRefId?: string,
  assignedTo?: string[],
  assignedBy?: string,
  completedBy?: string,
  deadline?: boolean | string,
  deadlineOrder?: string,
  trigger?: string[],
  type?: string[],
  from?: string,
  to?: string,
  status?: string[],
  sorting?: ITaskSortDTO
}

export class TaskFilter extends Base64QueryFilter<ITaskFilter> {
  id?: number;
  userGgId?: string;
  clientId?: number;
  opportunityRefId?: string;
  assignedTo?: string[] = [];
  assignedBy?: string;
  completedBy?: string;
  deadline?: boolean | string;
  deadlineOrder?: string;
  trigger?: string[] = [];
  type?: string[];
  from?: string;
  to?: string;
  status?: string[] = ["pending", "in-progress"];
  sorting?: ITaskSortDTO = { sortBy: "deadline", order: "asc" };

  constructor(data?: ITaskFilter) {
    super();
    if (data) {
      this.id = data.id;
      this.userGgId = data.userGgId;
      this.clientId = data.clientId;
      this.opportunityRefId = data.opportunityRefId;
      this.assignedTo = data.assignedTo ?? [];
      this.assignedBy = data.assignedBy;
      this.completedBy = data.completedBy;
      this.deadline = data.deadline;
      this.deadlineOrder = data.deadlineOrder;
      this.trigger = data.id ? [] : data.trigger ?? ["pending"];
      this.type = data.type;
      this.from = data.from;
      this.to = data.to;
      this.status = data.status;
      this.sorting = data.sorting ?? { sortBy: "deadline", order: "asc" };
    }
  }
}

export interface IGetTaskQuery {
  page: number
  pageSize: number
}

export interface IFindTaskQuery extends IGetTaskQuery {
  findQuery: ITaskFilter
}

export class PaginatedTasksDTO implements IPaginatedDTO<TaskDTO> {
  page: number = 1;
  total: number;
  elements: TaskDTO[];
  pageSize: number = 20;
  pageLength: number;

  constructor(data: IPaginatedDTO<ITaskDTO>) {
    this.page = data.page || 1;
    this.total = data.total;
    this.elements = data.elements.map(el => new TaskDTO(el));
    this.pageSize = data.pageSize || 20;
    this.pageLength = Math.ceil(data.total / this.pageSize);
  };
}

export interface ISaveTaskResponse {
  taskId: string
};

export interface ICompleteTaskDTO {
  taskId: number
  message?: string
  deadline?: string
  createdOpportunityRefId?: string
  completedBy: string
  taggedGgIds: string[]
  isReply: boolean
  createDataUponCompletion: boolean
}

export interface INotExecuteTaskDTO {
  leaderGgId: string
  completeTaskDTO: ICompleteTaskDTO
}

export interface TaskWithoutExecutionFormDTO {
  operationLeader: string,
  taskWithoutExecutionReason: string,
  taskWithoutExecutionDescription: string
}

export const taskPriorities: Array<{ text: string, value: number }> = [
  { text: "1", value: 1 },
  { text: "2", value: 2 },
  { text: "3", value: 3 },
  { text: "4", value: 4 },
  { text: "5", value: 5 },
  { text: "6", value: 6 },
  { text: "7", value: 7 },
  { text: "8", value: 8 },
  { text: "9", value: 9 },
  { text: "10", value: 10 }
];

export const taskDeadlines = ():INumberSelectOption[] => {
  return Array.from({ length: 72 }, (_, i) => i + 1).reduce((acc, curr) => {
    if ((curr - 1) % 24 === 0 && (curr - 1) / 24 > 0) {
      acc.push({ header: `${(curr - 1) / 24} Day +` } as INumberSelectOption);
    }

    acc.push({
      text: curr > 1 ? `${curr} hours` : "1 hour",
      value: curr
    });

    return acc;
  }, [{
    header: "Current day"
  }] as INumberSelectOption[]);
};

export const taskStatuses: IStringSelectOption[] = [
  { text: "Completed", value: "completed" },
  { text: "In progress", value: "in-progress" },
  { text: "Pending", value: "pending" }
];

export interface ITaskActionsDTO {
  name: string,
  action: string,
  icon: string,
  color: string,
  enabled: boolean,
  options?: string[]
}

export interface ISaveTaskPropertiesDTO {
  taskTrigger: string;
  deadline: number;
  priority: number;
  title?: string;
  content?: string;
  isNightSensitive: boolean;
  isSundaySensitive: boolean;
  isUrgent?: boolean;
  isManual?: boolean;
  assigneeGgId?: string;
  serviceCodes: number[];
  expectedExecutionTime: number;
  triggerLimit?: number;
  creationFrequency?: number;
  nullableAssignee: boolean;
  name?: string;
}

export interface ITaskPropertiesDTO {
  id: number;
  taskTrigger: string;
  deadline: number;
  priority: number;
  title?: string;
  content?: string;
  isNightSensitive: boolean;
  isSundaySensitive: boolean;
  isUrgent: boolean;
  isManual: boolean;
  assigneeGgId?: string;
  serviceCodes: number[];
  expectedExecutionTime: number;
  triggerLimit?: number;
  creationFrequency?: number;
  created: Date;
  updated?: Date;
  unshipped?: Date;
  name?: string;
}

export interface IUpdateTaskPropertiesDTO {
  taskTrigger?: string | null;
  deadline?: number | null;
  priority?: number | null;
  title?: string | null;
  content?: string | null;
  isNightSensitive?: boolean | null;
  isSundaySensitive?: boolean | null;
  isManual?: boolean | null;
  assigneeGgId?: string | null;
  serviceCodes?: number[] | null;
  expectedExecutionTime?: number | null;
  triggerLimit?: number | null;
  creationFrequency?: number | null;
  isUrgent?: boolean;
  nullableAssignee?: boolean | null;
  operatorGgId?: string;
  name?: string | null;
}

export interface IFindTaskPropertyByTaskTriggerDTO {
  taskTrigger: string;
}

export interface IReassignTaskDTO {
  assignorGgId: string,
  newOperatorGgId: string,
  comment?: string
};

export interface ITaskTriggerOptionDTO {
  text: string;
  value: string;
};

export enum TaskTrigger {
  AGILE_JOB_POST_MODIFIED = "agile-job-post-modified",
  APPROVE_SELF_SERVICE_OPP = "approve_self_service_opp",
  AGILE_TALENT_SEEKER_POSTED_SELF_SERVICE_JOB = "agile-talent-seeker-posted-self-service-job",
  CANDIDATE_INSPECTION = "candidate-inspection",
  CANDIDATE_MOVED_TO_INTERVIEW_STAGE = "candidate-moved-to-interview-stage",
  CANDIDATE_MOVED_TO_OFFER_STAGE = "candidate-moved-to-offer-stage",
  CANDIDATE_MOVED_TO_HIRE_STAGE = "candidate-moved-to-hire-stage",
  CANDIDATE_RECRUITER_ASSIGNED_OPPORTUNITY = "candidate-recruiter-assigned-opportunity",
  CANDIDATE_RECRUITER_CANDIDATE_INSPECTION = "candidate-recruiter-candidate-inspection",
  CANDIDATE_RECRUITER_TASK_COMPLETION_INSPECTION = "candidate-recruiter-task-completion-inspection",
  CLIENT_INSPECTION = "client-inspection",
  COMPENSATION_DIAGNOSIS_INSPECTION = "compensation-diagnosis-inspection",
  DATA_POINT_SUBMITTED = "data-point-submitted",
  GOLD_STAR_FEEDBACK_CANDIDATE_AGILE = "gold-star-feedback-candidate-agile",
  GOLD_STAR_FEEDBACK_CANDIDATE_NON_AGILE = "gold-star-feedback-candidate-non-agile",
  GOLD_STAR_FEEDBACK_TALENT_SEEKER_AGILE = "gold-star-feedback-talent-seeker-agile",
  GOLD_STAR_FEEDBACK_TALENT_SEEKER_NON_AGILE = "gold-star-feedback-talent-seeker-non-agile",
  INCOMPLETE_CANDIDATE_APPLICATIONS = "incomplete-candidate-applications",
  JOB_OPENING_APPROVED = "job-opening-approved",
  JOB_OPENING_INSPECTION = "job-opening-inspection",
  JOB_POST_SETUP_INSPECTION = "job-post-setup-inspection",
  MANUAL_TASK_CREATION = "manual-task-creation",
  INTERVIEW_CANDIDATES = "interview-candidates",
  INVITE_CANDIDATES = "invite-candidates",
  NEW_MESSAGE_RECEIVED = "new-message-received",
  NEW_EMAIL_MESSAGE_RECEIVED = "new-email-message-received",
  NEW_SUPPORT_EMAIL_MESSAGE = "new-support-email-message",
  NEW_SUPPORT_MESSENGER_MESSAGE = "new-support-messenger-message",
  NPS_FEEDBACK = "nps-feedback-request",
  NEW_JOB_POSTER = "new-job-poster",
  NYAX_CONVERSATION_INSPECTION = "nyax-conversation-inspection",
  OPP_HIRE_REPORTED = "opp-hire-reported",
  OPP_IN_END_STATE = "opp-in-end-state",
  OPP_IN_EVALUATION_STATE = "opp-in-evaluation-state",
  OPPORTUNITY_CREATION_REQUESTED = "opportunity-creation-requested",
  PIPELINE_INSPECTION = "pipeline-inspection",
  PLATINO_INSPECTION = "platino-inspection",
  QA_FEEDBACK = "qa-feedback",
  RE_APPROVE_SELF_SERVICE_OPP = "re_approve_self_service_opp",
  RECRUITER_BOT_CONVERSATION_REASSIGN = "recruiter-bot-manual-reassign",
  RECRUITER_BOT_CONVERSATION_EMAIL = "recruiter-bot-conversation-email",
  RECRUITERS_TASK_COMPLETION_INSPECTION = "recruiters-task-completion-inspection",
  REPORT_MENTIONS_REVIEWED = "report-mentions-reviewed",
  REVIEW_NEW_SKILL = "review-new-skill",
  REVIEW_SKILL_CONNECTION = "review-skill-connection",
  SELF_SERVICE_OPPORTUNITY_REOPENED = "self-service-opportunity-reopened",
  SET_OPPORTUNITY_COMPENSATION_RANGE = "set-opportunity-compensation-range",
  SOURCING_INSPECTION = "sourcing-inspection",
  SUCCESS_FEE_INVOICE_REQUESTED = "success-fee-invoice-requested",
  SYNDICATION_AGAIN = "syndication-again",
  SYNDICATION_INSPECTION = "syndication-inspection",
  TORRE_CREDIT_REQUESTED = "torre-credit-requested",
  USER_ACCOUNT_DELETED = "user-account-deleted",
  REVIEW_RELEVANT_MENTION_IN_SOCIAL_MEDIA = "review-relevant-mention-in-social-media",
  NEW_AGILE_CLIENT = "new-agile-client",
  EXTERNAL_NETWORKS = "ext-networks",
  EXTERNAL_SOURCING = "ext-sourcing",
  PAID_EXTERNAL_NETWORK = "paid-ext-networks",
  NEW_HELP_MESSAGE_RECEIVED = "new-help-message-received",
  OTTO_RECRUITER_BOT_EMAIL = "otto-recruiter-bot-email",
  OTTO_RECRUITER_BOT_MESSENGER = "otto-recruiter-bot-messenger",
  OTTO_RECRUITER_BOT_WHATSAPP = "otto-recruiter-bot-whatsapp",
  OPPORTUNITY_TESTIMONIAL_FEEDBACK = "opportunity-testimonial-feedback",
  NEW_QAT_MESSAGE = "new-qat-message",
  INSPECTION_TASK_COMPLETED_WITHOUT_EXECUTION = "inspection-task-completed-without-execution",
  SUPPORT_MESSENGER_TICKET_MESSAGE = "support-messenger-ticket-message",
  LINKED_IMPORT_REQUEST = "li-import-request",
  LINKED_IMPORT_REQUEST_PIC_NOT_FOUND = "li-import-request-picture-not-found",
  LINKED_IMPORT_REQUEST_CRAWLING_ERRORS = "li-import-request-crawling-errors",
  JOB_POST_NON_COMPLIANT = "job-post-non-compliant",
  IDENTITY_VERIFICATION_NAME_REVIEW = "identity-verification-name-review",
  IDENTITY_VERIFICATION_COUNTRY_REVIEW = "identity-verification-country-review",
  ORGANIZATION_VERIFICATION_WEBSITE_REVIEW = "organization-verification-website-review",
  WELCOME_BLOCKED_NOTIFICATION = "welcome-blocked-notification",
  SOURCING_CONNECTION_REQUEST = "sourcing-connection-request",
  NEW_CANDIDATE_TO_CONNECT_FROM_PROVIDERS = "new-candidate-to-connect-from-providers",
  EMMA_PITCH_MANUAL_CONTACT_RETRIEVAL = "emma-pitch-manual-contact-retrieval",
  SOURCING_MANUAL_RETRY = "sourcing-manual-retry",
  SOURCING_RETRY = "sourcing-retry",
  MESSAGE_TASK_30_MIN_FOLLOW_UP = "message-task-30-minutes-follow-up",
  MESSAGE_TASK_600_MIN_FOLLOW_UP = "message-task-600-minutes-follow-up",
  NEW_MESSAGE_SENT = "new-message-sent",
  REACH_SOURCE_SELECTION = "reach-source-selection",
  NEW_REACH_DISTRIBUTION_REQUIRED = "new-reach-distribution-required",
  NEW_REACH_DISTRIBUTION_REQUIRED_SOCIAL_MEDIA = "new-reach-distribution-required-social-media",
  NEW_REACH_DISTRIBUTION_SOURCING_REQUIRED = "new-reach-distribution-sourcing-required",
  NEW_REACH_DISTRIBUTION_COMMUNITIES_REQUIRED = "new-reach-distribution-communities-required",
  NEW_REACH_DISTRIBUTION_REQUIRED_INDEED = "new-reach-distribution-required-indeed",
  NEW_REACH_DISTRIBUTION_REQUIRED_ZIPPIA = "new-reach-distribution-required-zippia"
};

export const taskTriggers: IStringOption[] = [
  { value: TaskTrigger.AGILE_TALENT_SEEKER_POSTED_SELF_SERVICE_JOB },
  { value: TaskTrigger.MANUAL_TASK_CREATION },
  { value: TaskTrigger.OPP_HIRE_REPORTED },
  { value: TaskTrigger.OPP_IN_END_STATE },
  { value: TaskTrigger.APPROVE_SELF_SERVICE_OPP },
  { value: TaskTrigger.RE_APPROVE_SELF_SERVICE_OPP },
  { value: TaskTrigger.CANDIDATE_RECRUITER_ASSIGNED_OPPORTUNITY },
  { value: TaskTrigger.CANDIDATE_MOVED_TO_INTERVIEW_STAGE },
  { value: TaskTrigger.CANDIDATE_MOVED_TO_OFFER_STAGE },
  { value: TaskTrigger.CANDIDATE_MOVED_TO_HIRE_STAGE },
  { value: TaskTrigger.JOB_OPENING_APPROVED },
  { value: TaskTrigger.JOB_POST_NON_COMPLIANT },
  { value: TaskTrigger.IDENTITY_VERIFICATION_NAME_REVIEW },
  { value: TaskTrigger.IDENTITY_VERIFICATION_COUNTRY_REVIEW },
  { value: TaskTrigger.ORGANIZATION_VERIFICATION_WEBSITE_REVIEW },
  { value: TaskTrigger.GOLD_STAR_FEEDBACK_CANDIDATE_AGILE },
  { value: TaskTrigger.GOLD_STAR_FEEDBACK_CANDIDATE_NON_AGILE },
  { value: TaskTrigger.GOLD_STAR_FEEDBACK_TALENT_SEEKER_AGILE },
  { value: TaskTrigger.GOLD_STAR_FEEDBACK_TALENT_SEEKER_NON_AGILE },
  { value: TaskTrigger.NPS_FEEDBACK },
  { value: TaskTrigger.NEW_JOB_POSTER },
  { value: TaskTrigger.AGILE_JOB_POST_MODIFIED },
  { value: TaskTrigger.EXTERNAL_NETWORKS },
  { value: TaskTrigger.EXTERNAL_SOURCING },
  { value: TaskTrigger.PAID_EXTERNAL_NETWORK },
  { value: TaskTrigger.DATA_POINT_SUBMITTED },
  { value: TaskTrigger.REPORT_MENTIONS_REVIEWED },
  { value: TaskTrigger.REVIEW_NEW_SKILL },
  { value: TaskTrigger.REVIEW_SKILL_CONNECTION },
  { value: TaskTrigger.INCOMPLETE_CANDIDATE_APPLICATIONS },
  { value: TaskTrigger.CANDIDATE_INSPECTION },
  { value: TaskTrigger.CLIENT_INSPECTION },
  { value: TaskTrigger.JOB_POST_SETUP_INSPECTION },
  { value: TaskTrigger.PIPELINE_INSPECTION },
  { value: TaskTrigger.RECRUITERS_TASK_COMPLETION_INSPECTION },
  { value: TaskTrigger.SYNDICATION_INSPECTION },
  { value: TaskTrigger.SOURCING_INSPECTION },
  { value: TaskTrigger.NYAX_CONVERSATION_INSPECTION },
  { value: TaskTrigger.CANDIDATE_RECRUITER_TASK_COMPLETION_INSPECTION },
  { value: TaskTrigger.CANDIDATE_RECRUITER_CANDIDATE_INSPECTION },
  { value: TaskTrigger.COMPENSATION_DIAGNOSIS_INSPECTION },
  { value: TaskTrigger.PLATINO_INSPECTION },
  { value: TaskTrigger.QA_FEEDBACK },
  { value: TaskTrigger.RECRUITER_BOT_CONVERSATION_REASSIGN },
  { value: TaskTrigger.OPPORTUNITY_CREATION_REQUESTED },
  { value: TaskTrigger.JOB_OPENING_INSPECTION },
  { value: TaskTrigger.SET_OPPORTUNITY_COMPENSATION_RANGE },
  { value: TaskTrigger.TORRE_CREDIT_REQUESTED },
  { value: TaskTrigger.SUCCESS_FEE_INVOICE_REQUESTED },
  { value: TaskTrigger.SELF_SERVICE_OPPORTUNITY_REOPENED },
  { value: TaskTrigger.USER_ACCOUNT_DELETED },
  { value: TaskTrigger.NEW_MESSAGE_RECEIVED },
  { value: TaskTrigger.REVIEW_RELEVANT_MENTION_IN_SOCIAL_MEDIA },
  { value: "review-approval-request" },
  { value: "criteria-approval-from-talent-seeker" },
  { value: "negotiation-support" },
  { value: "torre-support-agent-delayed" },
  { value: "torre-support-answer-delayed" },
  { value: "torre-support-problem-detected" },
  { value: "recruiter-bot-conversation-whatsapp" },
  { value: "recruiter-bot-conversation-messenger" },
  { value: TaskTrigger.RECRUITER_BOT_CONVERSATION_EMAIL },
  { value: "syndication-on-behalf-of-talent-seeker" },
  { value: TaskTrigger.NEW_AGILE_CLIENT },
  { value: TaskTrigger.NEW_HELP_MESSAGE_RECEIVED },
  { value: TaskTrigger.NEW_SUPPORT_EMAIL_MESSAGE },
  { value: TaskTrigger.NEW_SUPPORT_MESSENGER_MESSAGE },
  { value: TaskTrigger.OTTO_RECRUITER_BOT_MESSENGER },
  { value: TaskTrigger.OTTO_RECRUITER_BOT_EMAIL },
  { value: TaskTrigger.OTTO_RECRUITER_BOT_WHATSAPP },
  { value: TaskTrigger.OPPORTUNITY_TESTIMONIAL_FEEDBACK },
  { value: TaskTrigger.NEW_QAT_MESSAGE },
  { value: TaskTrigger.SYNDICATION_AGAIN },
  { value: TaskTrigger.INSPECTION_TASK_COMPLETED_WITHOUT_EXECUTION },
  { value: TaskTrigger.SUPPORT_MESSENGER_TICKET_MESSAGE },
  { value: TaskTrigger.LINKED_IMPORT_REQUEST },
  { value: TaskTrigger.LINKED_IMPORT_REQUEST_PIC_NOT_FOUND },
  { value: TaskTrigger.LINKED_IMPORT_REQUEST_CRAWLING_ERRORS },
  { value: TaskTrigger.WELCOME_BLOCKED_NOTIFICATION },
  { value: TaskTrigger.SOURCING_CONNECTION_REQUEST },
  { value: TaskTrigger.EMMA_PITCH_MANUAL_CONTACT_RETRIEVAL },
  { value: TaskTrigger.SOURCING_MANUAL_RETRY },
  { value: TaskTrigger.INTERVIEW_CANDIDATES },
  { value: TaskTrigger.INVITE_CANDIDATES },
  { value: TaskTrigger.OPP_IN_EVALUATION_STATE },
  { value: TaskTrigger.SOURCING_RETRY }
];

export const reachNonConnectionBasedSourceExecutionTaskTrigger: TaskTrigger[] = [
  TaskTrigger.NEW_REACH_DISTRIBUTION_REQUIRED,
  TaskTrigger.NEW_REACH_DISTRIBUTION_REQUIRED_SOCIAL_MEDIA,
  TaskTrigger.NEW_REACH_DISTRIBUTION_REQUIRED_INDEED,
  TaskTrigger.NEW_REACH_DISTRIBUTION_REQUIRED_ZIPPIA
];

export const reachActionBasedSourceExecutionTaskTrigger: TaskTrigger[] = [
  TaskTrigger.NEW_REACH_DISTRIBUTION_SOURCING_REQUIRED,
  TaskTrigger.NEW_REACH_DISTRIBUTION_COMMUNITIES_REQUIRED
];

export const tasksWithMessageTaskCompletionDialog: TaskTrigger[] = [
  TaskTrigger.NEW_MESSAGE_RECEIVED,
  TaskTrigger.NEW_HELP_MESSAGE_RECEIVED,
  TaskTrigger.NEW_SUPPORT_EMAIL_MESSAGE,
  TaskTrigger.NEW_SUPPORT_MESSENGER_MESSAGE,
  TaskTrigger.OTTO_RECRUITER_BOT_EMAIL,
  TaskTrigger.OTTO_RECRUITER_BOT_MESSENGER,
  TaskTrigger.OTTO_RECRUITER_BOT_WHATSAPP,
  TaskTrigger.NPS_FEEDBACK,
  TaskTrigger.GOLD_STAR_FEEDBACK_CANDIDATE_AGILE,
  TaskTrigger.GOLD_STAR_FEEDBACK_CANDIDATE_NON_AGILE,
  TaskTrigger.GOLD_STAR_FEEDBACK_TALENT_SEEKER_AGILE,
  TaskTrigger.GOLD_STAR_FEEDBACK_TALENT_SEEKER_NON_AGILE,
  TaskTrigger.OPPORTUNITY_TESTIMONIAL_FEEDBACK,
  TaskTrigger.NEW_QAT_MESSAGE,
  TaskTrigger.NEW_EMAIL_MESSAGE_RECEIVED,
  TaskTrigger.MESSAGE_TASK_30_MIN_FOLLOW_UP,
  TaskTrigger.MESSAGE_TASK_600_MIN_FOLLOW_UP
];

export enum TaskWithoutExecutionReason {
  BUG_REPORTED = "bug-reported",
  ENOUGH_CANDIDATES_ON_THE_PIPELINE = "enough-candidates-on-the-pipeline",
  JOB_OPENING_CLOSED = "job-opening-closed",
  JOB_OPENING_IN_EVALUATION_STATE = "job-opening-in-evaluation-state",
  OTHER = "other"
}

export const TaskWithoutExecutionReasons: IStringSelectOption[] = Object.keys(TaskWithoutExecutionReason).map((key) => {
  const keyOfServiceStatus = key as keyof typeof TaskWithoutExecutionReason;

  return {
    text: MapFormats(key, startCase, capitalize),
    value: TaskWithoutExecutionReason[keyOfServiceStatus]
  };
});
